/**
 * MaxAIReport - MAX 플랜 전용 AI 리포트
 * 
 * MAX 플랜 사용자를 위한 프리미엄 분석 리포트
 * - 삼각형 레이더 차트 (기획-리스크-테스트케이스)
 * - 테스트케이스 품질 분석
 * - 상세 인사이트
 */

import { useState, useMemo } from 'react';
import type { ReleaseDecision, ReleaseInput } from '../types';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import TriRadarChart from './TriRadarChart';
import TestCaseQualityCard from './TestCaseQualityCard';

interface MaxAIReportProps {
  result: ReleaseDecision;
  inputData: ReleaseInput | null;
  isOpen: boolean;
  onClose: () => void;
  createdAt?: string;  // 평가 생성 날짜
  createdWithPlan?: string;  // 평가 생성 시점의 플랜
}

function tokenizeLoose(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
}

function hasKeywordOverlap(a: string, b: string): boolean {
  const at = tokenizeLoose(a);
  const bt = new Set(tokenizeLoose(b));
  if (at.length === 0 || bt.size === 0) return false;
  return at.some((t) => bt.has(t));
}

export default function MaxAIReport({ result, inputData, isOpen, onClose, createdAt, createdWithPlan }: MaxAIReportProps) {
  const { lang } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'quality' | 'insights'>('overview');
  
  // 날짜 포맷팅
  const formattedDate = useMemo(() => {
    if (!createdAt) return null;
    const date = new Date(createdAt);
    return date.toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [createdAt, lang]);
  
  // breakdown에서 점수 추출
  const scores = useMemo(() => {
    const breakdown = result.breakdown ?? [];
    const planningItem = breakdown.find(b => 
      b.category.toLowerCase().includes('planning') || b.category_ko?.includes('기획')
    );
    const riskItem = breakdown.find(b => 
      b.category.toLowerCase().includes('risk') || b.category_ko?.includes('리스크')
    );
    const testItem = breakdown.find(b => 
      b.category.toLowerCase().includes('test') || b.category.toLowerCase().includes('qa') ||
      b.category_ko?.includes('테스트') || b.category_ko?.includes('QA')
    );

    const base = {
      planning: planningItem ? Math.round((planningItem.score / planningItem.max_score) * 100) : 70,
      risk: riskItem ? Math.round((riskItem.score / riskItem.max_score) * 100) : 65,
      test: testItem ? Math.round((testItem.score / testItem.max_score) * 100) : 75,
    };

    // “테스트케이스 근거”가 없으면, QA 상태만으로는 테스트 축 점수를 보수적으로 제한
    // (PASS/PASS만 선택했을 때 100%처럼 보이는 혼동 방지)
    const hasTestEvidence =
      !!inputData?.testcase_file ||
      (inputData?.testcase_files?.length ?? 0) > 0 ||
      (inputData?.suggested_testcases?.length ?? 0) > 0;

    const qaValues = Object.values(inputData?.qa_status ?? {}).map((v) => String(v ?? '').toLowerCase());

    const hasAny = qaValues.some((v) => v.trim().length > 0);
    const isNotEntered = (v: string) => v.includes('미입력') || v.includes('not entered');
    const isVerified = (v: string) => v.includes('검증 완료') || v === 'verified';
    const isPartiallyVerified = (v: string) => v.includes('일부 검증') || v.includes('partially verified');
    const isPartiallyUnverified = (v: string) => v.includes('일부 미검증') || v.includes('partially unverified');
    const isUnverified = (v: string) => v.includes('미검증') || v === 'unverified';
    const isFailed = (v: string) => v.includes('실패') || v === 'failed';
    const isPartiallyFailed = (v: string) => v.includes('일부 실패') || v.includes('partially failed');

    // 최악 상태 기준으로 상한 결정
    let cap = 20;
    if (hasAny) {
      if (qaValues.every((v) => isNotEntered(v))) cap = 20;
      else if (qaValues.some((v) => isFailed(v) || isPartiallyFailed(v) || isUnverified(v) || isPartiallyUnverified(v))) cap = 35;
      else if (qaValues.some((v) => isPartiallyVerified(v))) cap = 55;
      else if (qaValues.every((v) => isVerified(v) || isNotEntered(v))) cap = 70;
      else cap = 50;
    }

    const test = hasTestEvidence ? base.test : Math.min(base.test, cap);

    return {
      planning: base.planning,
      risk: base.risk,
      test,
    };
  }, [result, inputData]);
  
  // 테스트케이스 품질 분석 (실제 테스트케이스가 있을 때만 표시)
  const testCaseQuality = useMemo(() => {
    if (!inputData) {
      return {
        planningCoverage: { items: [], score: null as number | null },
        riskCoverage: { items: [], score: null as number | null },
        qualityScore: null as number | null,
        suggestions: [],
      };
    }

    // 실제 테스트케이스(= AI 제안 테스트케이스)가 없으면 분석 불가
    const suggested = inputData.suggested_testcases ?? [];
    if (suggested.length === 0) {
      return {
        planningCoverage: { items: [], score: null as number | null },
        riskCoverage: { items: [], score: null as number | null },
        qualityScore: null as number | null,
        suggestions: [
          lang === 'ko'
            ? 'AI 제안 테스트케이스를 생성하거나, 테스트케이스를 임포트/작성한 뒤 다시 확인하세요.'
            : 'Generate AI-suggested test cases or import/write test cases, then review again.',
        ],
      };
    }

    // 기획 의도 커버리지 (키워드 매칭 기반 best-effort)
    const planningItems = (inputData.planning_intent ?? []).map((intent) => {
      const related = suggested.filter((tc) => {
        const tcText = `${tc.title}\n${tc.description}`;
        // source가 planning이면 우선 포함, 아니더라도 키워드 매칭으로 판단
        const matchesByKeyword = hasKeywordOverlap(intent, tcText);
        const matchesBySource = tc.source === 'planning';
        return matchesByKeyword || matchesBySource;
      });
      return {
        intent,
        covered: related.some((tc) => tc.status === 'pass'),
        testCases: related.map((tc) => tc.id).slice(0, 6),
      };
    });

    // 리스크 커버리지 (키워드 매칭 기반 best-effort)
    const riskItems = (inputData.risks ?? []).map((risk) => {
      const related = suggested.filter((tc) => {
        const tcText = `${tc.title}\n${tc.description}`;
        // source가 risk면 우선 포함, 아니더라도 키워드 매칭으로 판단
        const matchesByKeyword = hasKeywordOverlap(risk, tcText);
        const matchesBySource = tc.source === 'risk';
        return matchesByKeyword || matchesBySource;
      });
      return {
        intent: risk,
        covered: related.some((tc) => tc.status === 'pass'),
        testCases: related.map((tc) => tc.id).slice(0, 6),
      };
    });

    const planningScore =
      planningItems.length > 0
        ? Math.round((planningItems.filter((i) => i.covered).length / planningItems.length) * 100)
        : null;
    const riskScore =
      riskItems.length > 0 ? Math.round((riskItems.filter((i) => i.covered).length / riskItems.length) * 100) : null;

    // 품질 점수 (coverage 데이터가 있어야 산정)
    const qualityScore =
      planningScore === null && riskScore === null
        ? null
        : Math.round((planningScore ?? 0) * 0.5 + (riskScore ?? 0) * 0.3 + scores.test * 0.2);

    // 개선 제안 생성 (coverage 데이터가 있을 때만)
    const suggestions: string[] = [];
    if (planningScore !== null && planningScore < 80) {
      suggestions.push(lang === 'ko' 
        ? '모든 기획 의도에 대응하는 테스트케이스를 추가하세요'
        : 'Add test cases covering all planning intents');
    }
    if (riskScore !== null && riskScore < 70) {
      suggestions.push(lang === 'ko'
        ? '식별된 리스크에 대한 테스트케이스가 부족합니다'
        : 'More test cases needed for identified risks');
    }
    if (scores.test < 70) {
      suggestions.push(lang === 'ko'
        ? '엣지 케이스 테스트를 보강하세요'
        : 'Strengthen edge case testing');
    }
    
    return {
      planningCoverage: { items: planningItems, score: planningScore },
      riskCoverage: { items: riskItems, score: riskScore },
      qualityScore,
      suggestions,
    };
  }, [inputData, scores.test, lang]);
  
  // 인사이트 생성
  const insights = useMemo(() => {
    const items = [];
    
    // 강점
    if (scores.planning >= 80) {
      items.push({
        type: 'strength' as const,
        title: lang === 'ko' ? '기획 완성도 우수' : 'Strong Planning',
        desc: lang === 'ko' 
          ? '기획 의도가 명확하고 구체적으로 정의되어 있습니다.'
          : 'Planning intent is clear and well-defined.',
      });
    }
    if (scores.risk >= 80) {
      items.push({
        type: 'strength' as const,
        title: lang === 'ko' ? '리스크 대응 충분' : 'Good Risk Management',
        desc: lang === 'ko'
          ? '주요 리스크가 식별되고 대응 방안이 마련되어 있습니다.'
          : 'Key risks are identified with mitigation plans.',
      });
    }
    if (scores.test >= 80) {
      items.push({
        type: 'strength' as const,
        title: lang === 'ko' ? '테스트 커버리지 양호' : 'Good Test Coverage',
        desc: lang === 'ko'
          ? '핵심 시나리오와 엣지 케이스가 잘 검증되었습니다.'
          : 'Core scenarios and edge cases are well tested.',
      });
    }
    
    // 약점
    if (scores.planning < 60) {
      items.push({
        type: 'weakness' as const,
        title: lang === 'ko' ? '기획 보완 필요' : 'Planning Needs Work',
        desc: lang === 'ko'
          ? '기획 의도를 더 구체적으로 정의해주세요.'
          : 'Planning intent needs more detail.',
      });
    }
    if (scores.risk < 60) {
      items.push({
        type: 'weakness' as const,
        title: lang === 'ko' ? '리스크 검토 필요' : 'Risk Review Needed',
        desc: lang === 'ko'
          ? '잠재적 리스크에 대한 추가 분석이 필요합니다.'
          : 'Additional risk analysis is recommended.',
      });
    }
    if (scores.test < 60) {
      items.push({
        type: 'weakness' as const,
        title: lang === 'ko' ? '테스트 보강 필요' : 'More Testing Required',
        desc: lang === 'ko'
          ? '테스트 커버리지를 높여주세요.'
          : 'Increase test coverage.',
      });
    }
    
    // 밸런스 분석
    const minScore = Math.min(scores.planning, scores.risk, scores.test);
    const maxScore = Math.max(scores.planning, scores.risk, scores.test);
    const balance = 100 - (maxScore - minScore);
    
    if (balance >= 80) {
      items.push({
        type: 'insight' as const,
        title: lang === 'ko' ? '균형 잡힌 준비 상태' : 'Balanced Readiness',
        desc: lang === 'ko'
          ? '기획, 리스크, 테스트 3가지 축이 고르게 충족되어 있습니다.'
          : 'Planning, Risk, and Test axes are well balanced.',
      });
    } else if (balance < 60) {
      items.push({
        type: 'insight' as const,
        title: lang === 'ko' ? '불균형 주의' : 'Imbalance Warning',
        desc: lang === 'ko'
          ? '특정 영역이 상대적으로 약합니다. 균형 있는 준비가 필요합니다.'
          : 'Some areas are relatively weak. Balanced preparation needed.',
      });
    }
    
    return items;
  }, [scores, lang]);
  
  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-2xl h-[680px] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border bg-gradient-to-r from-purple-500/10 to-blue-500/10">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg">✨</span>
              <h2 className="text-lg font-bold text-terminal-text">
                {lang === 'ko' ? 'AI 분석 리포트' : 'AI Analysis Report'}
              </h2>
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded-full">
                MAX
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {/* 플랜 배지 */}
              {createdWithPlan && (
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                  createdWithPlan === 'max' 
                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                    : createdWithPlan === 'team'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : createdWithPlan === 'pro'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-terminal-dim text-terminal-muted border border-terminal-border'
                }`}>
                  {createdWithPlan.toUpperCase()} {lang === 'ko' ? '플랜' : 'Plan'}
                </span>
              )}
              {/* 생성 날짜 */}
              {formattedDate && (
                <span className="text-xs text-terminal-muted/70">
                  {formattedDate} {lang === 'ko' ? '생성' : 'Created'}
                </span>
              )}
            </div>
            <p className="text-xs text-terminal-muted mt-0.5">
              {result.feature_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-terminal-muted hover:text-terminal-text transition-colors rounded-lg hover:bg-terminal-dim/30"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 탭 네비게이션 */}
        <div className="flex border-b border-terminal-border">
          {[
            { id: 'overview', label: lang === 'ko' ? '📊 종합 분석' : '📊 Overview' },
            { id: 'quality', label: lang === 'ko' ? '🔍 TC 품질' : '🔍 TC Quality' },
            { id: 'insights', label: lang === 'ko' ? '💡 인사이트' : '💡 Insights' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-terminal-text border-b-2 border-status-go'
                  : 'text-terminal-muted hover:text-terminal-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 종합 분석 탭 */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 삼각형 레이더 차트 */}
              <div className="flex justify-center">
                <TriRadarChart
                  planning={scores.planning}
                  risk={scores.risk}
                  testCoverage={scores.test}
                  size={280}
                />
              </div>
              
              {/* 각 축 상세 점수 */}
              <div className="grid grid-cols-3 gap-3">
                <ScoreCard
                  icon="📋"
                  label={lang === 'ko' ? '기획' : 'Planning'}
                  score={scores.planning}
                />
                <ScoreCard
                  icon="⚠️"
                  label={lang === 'ko' ? '리스크' : 'Risk'}
                  score={scores.risk}
                />
                <ScoreCard
                  icon="✅"
                  label={lang === 'ko' ? '테스트' : 'Test'}
                  score={scores.test}
                />
              </div>
              
              {/* 요약 */}
              <div className="p-4 bg-terminal-dim/10 rounded-lg border border-terminal-border">
                <h4 className="text-sm font-semibold text-terminal-text mb-2">
                  {lang === 'ko' ? '📝 요약' : '📝 Summary'}
                </h4>
                <p className="text-sm text-terminal-muted leading-relaxed">
                  {lang === 'ko'
                    ? `이 릴리즈의 3축 분석 결과, 기획(${scores.planning}점), 리스크 대응(${scores.risk}점), 테스트 커버리지(${scores.test}점) 영역에서 각각 평가되었습니다. ${
                        Math.min(scores.planning, scores.risk, scores.test) < 60
                          ? '일부 영역에서 개선이 필요합니다.'
                          : '전반적으로 양호한 준비 상태입니다.'
                      }`
                    : `Tri-axis analysis: Planning (${scores.planning}), Risk (${scores.risk}), Test Coverage (${scores.test}). ${
                        Math.min(scores.planning, scores.risk, scores.test) < 60
                          ? 'Some areas need improvement.'
                          : 'Overall readiness is good.'
                      }`
                  }
                </p>
              </div>
            </div>
          )}
          
          {/* TC 품질 탭 */}
          {activeTab === 'quality' && (
            <TestCaseQualityCard
              planningCoverage={testCaseQuality.planningCoverage}
              riskCoverage={testCaseQuality.riskCoverage}
              qualityScore={testCaseQuality.qualityScore}
              suggestions={testCaseQuality.suggestions}
            />
          )}
          
          {/* 인사이트 탭 */}
          {activeTab === 'insights' && (
            <div className="space-y-4">
              {insights.map((insight, i) => (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${
                    insight.type === 'strength'
                      ? 'bg-status-go/5 border-status-go/30'
                      : insight.type === 'weakness'
                      ? 'bg-status-danger/5 border-status-danger/30'
                      : 'bg-status-warning/5 border-status-warning/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">
                      {insight.type === 'strength' ? '💪' :
                       insight.type === 'weakness' ? '⚠️' : '💡'}
                    </span>
                    <div>
                      <h4 className={`text-sm font-semibold mb-1 ${
                        insight.type === 'strength' ? 'text-status-go' :
                        insight.type === 'weakness' ? 'text-status-danger' : 'text-status-warning'
                      }`}>
                        {insight.title}
                      </h4>
                      <p className="text-sm text-terminal-muted">
                        {insight.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {insights.length === 0 && (
                <div className="text-center py-8 text-terminal-muted">
                  {lang === 'ko' ? '분석 중...' : 'Analyzing...'}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-terminal-border bg-terminal-dim/20">
          <div className="flex items-center justify-between">
            <p className="text-xs text-terminal-muted">
              {lang === 'ko' 
                ? '✨ MAX 플랜 전용 프리미엄 분석'
                : '✨ MAX Plan exclusive premium analysis'}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-terminal-dim/30 hover:bg-terminal-dim/50 border border-terminal-border rounded-lg text-sm text-terminal-text transition-colors"
            >
              {lang === 'ko' ? '닫기' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 점수 카드 서브 컴포넌트
function ScoreCard({ icon, label, score }: { icon: string; label: string; score: number }) {
  const getScoreColor = (s: number) => {
    if (s >= 80) return 'text-status-go';
    if (s >= 60) return 'text-status-warning';
    return 'text-status-danger';
  };
  
  return (
    <div className="p-3 bg-terminal-dim/10 rounded-lg border border-terminal-border text-center">
      <span className="text-lg">{icon}</span>
      <div className="text-xs text-terminal-muted mt-1">{label}</div>
      <div className={`text-xl font-bold ${getScoreColor(score)}`}>{score}</div>
    </div>
  );
}

